import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Tag,
  Tags,
  Users,
  Truck,
  FileText,
  Wrench,
  Store,
  AlertTriangle,
  Calculator,
  Settings as SettingsIcon,
  LogOut,
  TrendingUp,
  Compass,
  ClipboardList,
  ScrollText,
  ShieldAlert,
  ClipboardCheck,
  History,
  RefreshCw,
  ShoppingBag,
  Grid3x3,
  DatabaseBackup,
  BookOpenCheck,
  PackageSearch,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth, logout } from "@/api/auth";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useModuleSettings } from "@/api/warehouse";

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  // Per-Item-Gating (zusaetzlich zum Gruppen-Gate) — z.B. Leiter-Listen nur fuer warehouse.lead,
  // waehrend die uebrigen Lager-Items schon bei warehouse.general sichtbar sind (Task 16).
  show?: (auth: ReturnType<typeof useAuth>) => boolean;
  // Nur exakt diese Route markieren (NavLink `end`) — sonst bleibt ein Eltern-Pfad wie
  // /preise unter /preise/verkaufspreis mit hervorgehoben.
  end?: boolean;
  // Unterpunkt des vorigen Eintrags (eingerückt).
  indent?: boolean;
}
interface NavGroup {
  title: string;
  items: NavItem[];
  adminOnly?: boolean;
  show?: (auth: ReturnType<typeof useAuth>) => boolean;
}

const GROUPS: NavGroup[] = [
  {
    title: "Überblick",
    items: [
      { to: "/overview", label: "Dashboard", icon: <LayoutDashboard className="size-4" /> },
      { to: "/analytics/articles", label: "Artikel-Analyse", icon: <TrendingUp className="size-4" /> },
      { to: "/analytics/reach", label: "Lager-Reichweite", icon: <Package className="size-4" /> },
      {
        to: "/einkauf",
        label: "Bestellungen",
        icon: <ShoppingBag className="size-4" />,
        show: (a) => !!a.user?.permissions?.article_purchase,
      },
    ],
  },
  {
    title: "Lager",
    show: (a) => a.warehouse.general,
    items: [
      // Reihenfolge nach Tagesablauf (User 2026-07-17): Leiter-Listen → Erstaufnahme →
      // Wareneingang → Nachschub → Journal, danach alles Uebrige. Offene Meldungen sind
      // der erste Blick des Leiters in die Schicht; Lager-Start ist nur die Startseite.
      // Lager-Navi (Plan 2026-10-23 S3) steht davor und buendelt Suche, Regale, Leere Plaetze und Plaetze.
      { to: "/warehouse/lager-navi", label: "Lager-Navi", icon: <Compass className="size-4" />, show: (a) => a.warehouse.general },
      { to: "/warehouse/leiter", label: "Leiter-Listen", icon: <ShieldAlert className="size-4" />, show: (a) => a.warehouse.lead },
      // RA-WI8: Wareneingang + Inventur sind GET-Listen auf Backend-Level 'general'
      // (nur die Aktionen darin sind 'lead'/'count') — vorher faelschlich auf
      // 'book'/'count' verengt, das sperrte einen reinen Leiter aus (Re-Audit-Fund).
      // Erstaufnahme (Modul ⑧): Board 'general' lesbar, Aktionen (Etiketten/Abgleich) inline lead-gated.
      { to: "/warehouse/erstaufnahme", label: "Erstaufnahme", icon: <History className="size-4" />, show: (a) => a.warehouse.general },
      { to: "/warehouse/wareneingang", label: "Wareneingang", icon: <ClipboardList className="size-4" />, show: (a) => a.warehouse.general },
      // Backend-Gate der Fillup-GET-Endpoints ist 'general' — Storno-Aktion darin bleibt lead-gated (Task 14).
      { to: "/warehouse/nachschub", label: "Kommissionieren", icon: <RefreshCw className="size-4" />, show: (a) => a.warehouse.general },
      // User 2026-10-15: Leiter finden das Journal als Reiter in den Leiter-Listen.
      { to: "/warehouse/journal", label: "Journal", icon: <ScrollText className="size-4" />, show: (a) => !a.warehouse.lead },
      // User 2026-10-24: Lager-Start, Einlagern und Umfahren nicht im Menue. Die Seiten bleiben
      // ueber ihre URL und die Kacheln auf /warehouse erreichbar.
      { to: "/warehouse/inventur", label: "Inventur", icon: <ClipboardCheck className="size-4" />, show: (a) => a.warehouse.general },
      // OI-130 P4 (User 2026-08-03): Handregal-Karte war nur als Kachel auf Lager-Start
      // erreichbar — der User suchte sie links und fand nichts.
      { to: "/warehouse/handregal", label: "Handregal-Karte", icon: <Grid3x3 className="size-4" />, show: (a) => a.warehouse.general },
      // User 2026-10-28: NULL-MENGEN war nur per URL bzw. Kachel auf dem (nicht verlinkten) Lager-Start erreichbar.
      { to: "/warehouse/nullmengen", label: "NULL-MENGEN", icon: <PackageSearch className="size-4" />, show: (a) => a.warehouse.lead },
      // OI-130 P5 (User 2026-08-03): Sicherung eigene Seite, nur fuer Lager-Admins sichtbar.
      // User 2026-10-24: Sicherung vorletzte, Mitarbeiter letzte Zeile.
      { to: "/warehouse/sicherung", label: "Sicherung", icon: <DatabaseBackup className="size-4" />, show: (a) => a.warehouse.admin },
      { to: "/warehouse/mitarbeiter", label: "Mitarbeiter", icon: <Users className="size-4" />, show: (a) => a.warehouse.lead },
    ],
  },
  {
    title: "Sortiment",
    items: [
      { to: "/catalog", label: "Übersicht-Grundartikel", icon: <Tag className="size-4" /> },
      // Preis-Rollout im Grundartikel S4 (Entscheid 18.10.2026): Unterpunkt der Grundartikel-Übersicht,
      // dieselbe globale Seite wie unter „Preise“ (ein Menüpunkt kennt keinen einzelnen Artikel).
      { to: "/preise/rollout", label: "Preis-Rollout", icon: <TrendingUp className="size-4" />, indent: true },
      { to: "/inventory", label: "Bestand", icon: <Package className="size-4" /> },
      {
        to: "/product-knowledge/service-cases",
        label: "Produktwissen / Servicefälle",
        icon: <BookOpenCheck className="size-4" />,
        show: (a) => !!a.user?.permissions?.article_general,
      },
    ],
  },
  {
    title: "Betrieb",
    items: [
      { to: "/orders", label: "Aufträge", icon: <ShoppingCart className="size-4" /> },
      { to: "/stock", label: "Bestandsalarme", icon: <AlertTriangle className="size-4" /> },
      { to: "/customers", label: "Kunden", icon: <Users className="size-4" /> },
      { to: "/invoices", label: "Rechnungen", icon: <FileText className="size-4" /> },
      { to: "/shipping", label: "Versand", icon: <Truck className="size-4" /> },
    ],
  },
  {
    title: "Preise",
    items: [
      { to: "/preise", label: "Kalkulation", icon: <Calculator className="size-4" />, end: true },
      { to: "/preise/verkaufspreis", label: "Verkaufspreis", icon: <Tags className="size-4" /> },
      { to: "/preise/einstellungen", label: "Versandeinstellungen", icon: <SettingsIcon className="size-4" /> },
      { to: "/preise/rollout", label: "Preis-Rollout", icon: <TrendingUp className="size-4" /> },
    ],
  },
  {
    title: "Admin",
    adminOnly: true,
    items: [
      { to: "/admin/users", label: "Benutzer-Admin", icon: <Wrench className="size-4" /> },
      { to: "/admin/marktplatzkonten", label: "Marktplatzkonten", icon: <Store className="size-4" /> },
    ],
  },
];

export function StahliaSidebar() {
  const auth = useAuth();
  const moduleSettings = useModuleSettings();
  const { user, isAdmin } = auth;
  const navigate = useNavigate();
  const initials = user?.email?.charAt(0).toUpperCase() ?? "?";

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <aside
      className="bg-panel border-r border-line h-screen sticky top-0 flex flex-col"
      style={{ width: "var(--sidebar-w)" }}
    >
      <div className="px-5 py-4 border-b border-line">
        <div className="flex items-center gap-2">
          <span className="font-display font-bold text-lg tracking-tight text-ink">
            stahlia
          </span>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary-soft text-primary">
            Admin
          </span>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
        {GROUPS
          .filter((g) => (!g.adminOnly || isAdmin) && (!g.show || g.show(auth)))
          .filter((g) => !auth.isWarehouseOnly || g.title === 'Lager')
          .map((group) => (
          <div key={group.title}>
            <div className="px-2 mb-2 text-[11px] uppercase tracking-wider text-muted">
              {group.title}
            </div>
            <ul className="space-y-0.5">
              {group.items
                .filter((item) => item.to !== '/warehouse/erstaufnahme' || moduleSettings.isError || moduleSettings.data?.erstaufnahme_sichtbar !== false)
                .filter((item) => !item.show || item.show(auth))
                .map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors",
                        item.indent && "ml-4 text-[13px]",
                        isActive
                          ? "bg-primary-soft text-primary font-medium"
                          : "text-ink-2 hover:bg-panel-2 hover:text-ink",
                      )
                    }
                  >
                    {item.icon}
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-line px-3 py-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2.5 w-full rounded-md px-2 py-1.5 hover:bg-panel-2 transition-colors text-left"
              title={user?.email ?? ""}
            >
              <div className="w-8 h-8 rounded-full bg-primary-soft text-primary flex items-center justify-center text-xs font-medium flex-shrink-0">
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs text-ink-2 truncate">{user?.email}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-48">
            <DropdownMenuItem
              onClick={() => void handleLogout()}
              className="gap-2 cursor-pointer"
            >
              <LogOut className="size-4" />
              Abmelden
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
